import { useNavigate } from 'react-router-dom'

import { F0Alert } from '@factorialco/f0-react'
import { InfoCircle } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface BudgetInfo {
  id: string
  name: string
  amountCents: number
  spentAmountCents: number
  availableAmountCents: number
  currency: string
}

interface BudgetLinkBannerProps {
  budgetId: string | null
  selectedBudget: BudgetInfo | null
  totalGroupCostCents: number
  hasBudgets: boolean
  trainingCurrency: string
  currentLinkedAmountCents: number
  canManageBudgets?: boolean
  isLinkedBudgetArchived?: boolean
}

export const BudgetLinkBanner = ({
  budgetId,
  selectedBudget,
  totalGroupCostCents,
  hasBudgets,
  trainingCurrency,
  currentLinkedAmountCents,
  canManageBudgets,
  isLinkedBudgetArchived,
}: BudgetLinkBannerProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    access: { locale },
  } = useCurrentSession()

  const goToBudgets = () => navigate(navigation.trainings.budgets.list.fullPath())
  const goToBudget = () =>
    budgetId && navigate(navigation.trainings.budgets.detail.fullPath({ budgetId }))

  // No budgets exist
  if (!hasBudgets) {
    if (!canManageBudgets) return null

    return (
      <F0Alert
        variant="neutral"
        title={i18n.t('trainings.classes.costs.banner.no_budgets_created_title')}
        description={i18n.t('trainings.classes.costs.banner.no_budgets_created')}
        action={{
          label: i18n.t('trainings.classes.costs.banner.create_budget'),
          onClick: () => navigate(navigation.trainings.budgets.new.fullPath()),
        }}
      />
    )
  }

  // No budget selected — only show the "add to budget" prompt if user can manage budgets
  if (!budgetId || !selectedBudget) {
    if (!canManageBudgets) return null

    return (
      <F0Alert
        variant="neutral"
        title={i18n.t('trainings.classes.costs.banner.no_budget_selected_title')}
        description={i18n.t('trainings.classes.costs.banner.add_to_budget')}
        action={{
          label: i18n.t('trainings.classes.costs.banner.view_budgets'),
          onClick: goToBudgets,
        }}
      />
    )
  }

  // Linked budget is archived
  if (isLinkedBudgetArchived && selectedBudget) {
    return (
      <F0Alert
        variant="neutral"
        icon={InfoCircle}
        title={i18n.t('trainings.classes.costs.banner.archived_budget_title')}
        description={i18n.t('trainings.classes.costs.banner.archived_budget_description')}
        action={{
          label: i18n.t('trainings.classes.costs.banner.view_budget'),
          onClick: goToBudget,
        }}
      />
    )
  }

  // Calculate if over budget
  // When already linked, availableAmountCents has our cost subtracted - add it back
  const availableCents = Number(selectedBudget.availableAmountCents) || 0
  const effectiveAvailable = availableCents + currentLinkedAmountCents
  const isOverBudget = totalGroupCostCents > effectiveAvailable

  // Within budget
  if (!isOverBudget) {
    return (
      <F0Alert
        variant="positive"
        title={i18n.t('trainings.classes.costs.banner.within_budget_title')}
        description={i18n.t('trainings.classes.costs.banner.within_budget')}
        action={{
          label: i18n.t('trainings.classes.costs.banner.view_budget'),
          onClick: goToBudget,
        }}
      />
    )
  }

  // Over budget - show warning alert
  const budgetAmountCents = Number(selectedBudget.amountCents) || 0
  const overrunCents = Math.round(totalGroupCostCents - effectiveAvailable)
  const overrunPercentage =
    budgetAmountCents > 0 ? ((overrunCents / budgetAmountCents) * 100).toFixed(1) : '0.0'

  return (
    <F0Alert
      variant="warning"
      title={i18n.t('trainings.classes.costs.banner.budget_overrun_title')}
      description={i18n.t('trainings.classes.costs.banner.budget_overrun_description', {
        budgetName: selectedBudget.name,
        amount: amountWithCurrency(overrunCents, selectedBudget.currency, locale),
        percentage: overrunPercentage,
      })}
      action={{ label: i18n.t('trainings.classes.costs.banner.view_budget'), onClick: goToBudget }}
    />
  )
}
